import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragObject } from '@lmt/unicorn-shared/app/components/calendar/devextreme/common';
import { MouseButton } from '@lmt/unicorn-shared/app/components/common/types';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTitleAndIconForTab } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { closeTabIcon } from '@lmt/unicorn-shared/app/helpers/cached-images';
import React, { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './NavTab.module.scss';

interface Props {
  path: string;
  id?: string;
  search?: string;

  isSelected: boolean;
  onClose: (id: string, isSelected: boolean) => void;
  isFixedTab?: boolean;
}

const isTab = (path: string) => {
  return !!pages.tabs.matchPath(path);
};

export const getPatientIdFromPath = (path: string) => {
  const pathParts = path.split('/');
  const index = pathParts.findIndex((part) => part === 'patients');
  const patientId = parseInt(pathParts[index + 1]);
  if (index > 0 && !isNaN(patientId)) {
    return patientId;
  }
  return null;
};

export const NavTab: FC<Props> = React.memo(function NavTab(props) {
  const { isSelected, onClose, isFixedTab, path, search: urlSearch } = props;
  const navigate = useNavigate();

  const isTabPath = isTab(props.path);

  const {
    title,
    icon: Icon,
    type,
    selectedIcon: SelectedIcon,
  } = useTitleAndIconForTab(
    props.path,
    urlSearch,
    isFixedTab, // for fixed tabs use default title
  );
  const item: DragObject = useMemo(() => {
    const patientId = getPatientIdFromPath(path);
    const baseInfo = {
      title: title,
      icon: SelectedIcon,
    };
    if (!patientId) {
      return {
        ...baseInfo,
        type: 'generic-tab',
      };
    }
    const tabWithPatient: DragObject = {
      ...baseInfo,
      type: 'patient',
      viewType: 'tab',
      patientId: patientId,
    };

    return tabWithPatient;
  }, [path, title]);

  const sortable = useMemo(() => ({ id: props.id!, data: item }), [item]);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable(sortable);

  const style = {
    transform: CSS.Transform?.toString(transform),
    transition: transition ?? undefined,
  };

  const sortableProps = props.id
    ? {
        ref: setNodeRef,
        style,
        ...attributes,
        ...listeners,
      }
    : undefined;
  const CloseTabIcon = closeTabIcon;
  return (
    <div
      {...sortableProps}
      data-tab={isTabPath}
      className={styles.navTabContainer}
      data-selected={isSelected}
      data-type={type}
      onClick={() =>
        !isSelected && navigate({ pathname: props.path, search: props.search })
      }
      onMouseDown={(e) => {
        // close tab by middle button
        if (e.button === MouseButton.Middle && props.id) {
          onClose(props.id, isSelected);
        }
      }}
    >
      {isSelected
        ? SelectedIcon && <SelectedIcon className={styles.tabIcon} />
        : Icon && <Icon className={styles.tabIcon} />}
      <div
        className={isFixedTab ? styles.fixedNavTab : styles.navTab}
        data-selected={isSelected}
        data-type={type}
        title={title}
      >
        {title}
      </div>
      {!isFixedTab && props.id && isSelected && (
        <a
          onClick={() => onClose(props.id!, true)}
          className={styles.closeTab}
          data-test-id="close"
        >
          <CloseTabIcon className={styles.closeTabIcon} />
        </a>
      )}
    </div>
  );
});

export function getPathWithoutTabPrefix(path: string) {
  return `/${path.split('/').slice(3).join('/')}`;
}
